import { useQuery } from '@tanstack/react-query'
import FbButton from 'components/FbUI/FbButton'
import { Modal } from 'components/UI/Modal'
import { AlertTriangle, Briefcase, Send, Star } from 'lucide-react'
import React from 'react'
import useApiService, { BulkDeleteContactsPreview } from 'services/api'

interface Props {
  show: boolean
  onClose: () => void
  onConfirm: () => void
  isDeleting: boolean
  count: number
  contactIds: number[]
  isAllRowsSelected: boolean
  filterParams: Record<string, unknown>
}

/**
 * Modal for confirming bulk deletion of contacts.
 *
 * Fetches a preview of deletion impact (emails, deals, sequences, starred)
 * via the preview endpoint and displays counts as contextual bullets.
 * Falls back gracefully if the preview request fails — deletion remains enabled.
 */
export function BulkDeleteContactsModal(props: Props): React.ReactElement {
  const api = useApiService()

  const { data, isLoading, isError } = useQuery<BulkDeleteContactsPreview>({
    queryKey: [
      'bulk-delete-contacts-preview',
      props.contactIds,
      props.isAllRowsSelected,
      props.filterParams,
    ],
    queryFn: () =>
      api.bulkDeleteContactsPreview(
        props.contactIds,
        props.isAllRowsSelected,
        props.filterParams
      ),
    enabled: props.show,
    retry: 0,
  })

  const pluralS = props.count === 1 ? '' : 's'
  const title = `Delete ${props.count} contact${pluralS}?`
  const confirmLabel = `Delete ${props.count} contact${pluralS}`

  const hasWarnings =
    !!data &&
    (data.in_active_sequences > 0 ||
      data.contacts_with_deals > 0 ||
      data.starred > 0)

  return (
    <Modal show={props.show} onHide={props.onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="py-6 text-center text-fg-secondary">
            Calculating impact…
          </div>
        )}
        {isError && (
          <div className="py-4 text-sm text-fg-secondary">
            We couldn&apos;t calculate the impact for this selection. You can
            still continue — the deletion will be performed as requested.
          </div>
        )}
        {data && (
          <div className="space-y-3">
            <p className="text-sm text-fg">
              This will permanently delete the selected contacts and all
              associated data.
            </p>

            {hasWarnings && (
              <div className="flex flex-col gap-2 rounded-lg bg-surface-alt p-3">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-fg">
                  <AlertTriangle className="size-3.5 shrink-0 text-fg-secondary" />
                  Heads up — some of these contacts have active relationships
                </p>
                <ul className="flex flex-col gap-1.5 text-xs text-fg">
                  {data.in_active_sequences > 0 && (
                    <li className="flex items-start gap-2">
                      <Send className="mt-0.5 size-3.5 shrink-0 text-fg-secondary" />
                      <span>
                        <b>{data.in_active_sequences}</b>{' '}
                        {data.in_active_sequences === 1 ? 'is' : 'are'} in an
                        active email sequence — they&apos;ll stop receiving
                        emails
                      </span>
                    </li>
                  )}
                  {data.contacts_with_deals > 0 && (
                    <li className="flex items-start gap-2">
                      <Briefcase className="mt-0.5 size-3.5 shrink-0 text-fg-secondary" />
                      <span>
                        <b>{data.contacts_with_deals}</b>{' '}
                        {data.contacts_with_deals === 1 ? 'is' : 'are'} linked
                        to <b>{data.deals_linked}</b>{' '}
                        {data.deals_linked === 1 ? 'deal' : 'deals'} — the{' '}
                        {data.deals_linked === 1 ? 'deal stays' : 'deals stay'}{' '}
                        without them
                      </span>
                    </li>
                  )}
                  {data.starred > 0 && (
                    <li className="flex items-start gap-2">
                      <Star className="mt-0.5 size-3.5 shrink-0 text-fg-secondary" />
                      <span>
                        <b>{data.starred}</b>{' '}
                        {data.starred === 1 ? 'is' : 'are'} starred
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <FbButton
          type="button"
          variant="secondary"
          onClick={props.onClose}
          disabled={props.isDeleting}
        >
          Cancel
        </FbButton>
        <FbButton
          type="button"
          variant="danger"
          onClick={props.onConfirm}
          disabled={props.isDeleting || isLoading}
        >
          {confirmLabel}
        </FbButton>
      </Modal.Footer>
    </Modal>
  )
}
